/**
 * Returns the base URL of the app. Works on both client and server as we use NEXT_PUBLIC_ variables.
 *
 * Reminder:
 *  - "base url" includes the protocol (e.g., https://example.com)
 *  - "hostname" doesn't include the protocol (e.g., example.com)
 *
 * Priority order:
 * 1. Staging environment: If NEXT_PUBLIC_VERCEL_TARGET_ENV is 'staging',
 *    use env.COUPLESIDE_STAGING_URL. Note: env.COUPLESIDE_STAGING_URL is expected to include the protocol (e.g., https://...).
 *
 * 2. Live environment: If env.LIVE is true, use env.COUPLESIDE_URL.
 *    env.LIVE is true for both live and staging! This is why the staging URL check is first.
 *
 * 3. Preview/branch deployments: If Vercel target env is "preview" and not master, and branch url exists
 *    (e.g., web-git-feat-branch-name-123456-bridebookhq.vercel.app), use it with https:// prefix.
 *    See: https://vercel.com/docs/environment-variables/framework-environment-variables#NEXT_PUBLIC_VERCEL_BRANCH_URL
 *
 * 4. Local development fallback: If none of the above, use env.COUPLESIDE_URL
 *    (usually set to http://localhost:3000 via .env.development.local or .env.local).
 *
 * To make it work on localhost, set NEXT_PUBLIC_COUPLESIDE_URL to http://localhost:3000
 */
export function getBaseUrlFromEnvs(): string {
  const isStaging = process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV === 'staging';
  if (isStaging) return env.COUPLESIDE_STAGING_URL;

  // Live environment uses the production URL
  if (env.LIVE) return env.COUPLESIDE_URL;

  const isPreview =
    process.env.NEXT_PUBLIC_VERCEL_TARGET_ENV === 'preview' &&
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF !== 'master';
  const branchUrl = process.env.NEXT_PUBLIC_VERCEL_BRANCH_URL;
  if (isPreview && branchUrl) return `https://${branchUrl}`;

  return env.COUPLESIDE_URL;
}

export const env = {
  API_ZENDESK_WIDGET: process.env.NEXT_PUBLIC_API_ZENDESK_WIDGET as string,
  BB_S3_IMAGE_REGION: process.env.BB_S3_IMAGE_REGION || 'us-west-1',
  CMS_URL: process.env.NEXT_PUBLIC_CMS_URL as string,
  COUPLESIDE_STAGING_URL: process.env.NEXT_PUBLIC_COUPLESIDE_STAGING_URL as string,
  COUPLESIDE_URL: process.env.NEXT_PUBLIC_COUPLESIDE_URL as string,
  CUSTOMER_IO_API_KEY: process.env.CUSTOMER_IO_API_KEY as string,
  CUSTOMER_IO_SITE_ID: process.env.CUSTOMER_IO_SITE_ID as string,
  CUSTOMERIO_APP_API_KEY: process.env.CUSTOMERIO_APP_API_KEY,
  ELASTIC_ARTICLES: process.env.ELASTIC_ARTICLES as string,
  ELASTIC_ARTICLES_NEW: process.env.ELASTIC_ARTICLES_NEW as string,
  FIREBASE: { databaseURL: process.env.NEXT_PUBLIC_FIREBASE_DATABASE_URL as string },
  GCP_LIVE: process.env.GCLOUD_PROJECT === 'bridebookhq',
  IS_PRODUCTION_BUILD: process.env.NODE_ENV === 'production',
  LIVE: process.env.NEXT_PUBLIC_DATABASE === 'production',
  MIN_ARTICLES_COUNT: process.env.MIN_ARTICLES_COUNT as string,
  MONITORING_TOKEN: process.env.MONITORING_TOKEN || '',
  /**
   * The AI planner's own Redis instance, kept apart from the shared cache so a
   * cached template read can never evict a coach memory. No URL counterpart —
   * that field exists on the shared config only for Heroku credential rotation.
   */
  R2_ACCESS_KEY_ID: process.env.R2_ACCESS_KEY_ID || '',
  R2_SECRET_ACCESS_KEY: process.env.R2_SECRET_ACCESS_KEY || '',
  REDIS_HOST: process.env.REDIS_HOST as string,
  REDIS_PASS: process.env.REDIS_PASS || '',
  REDIS_PORT: process.env.REDIS_PORT as string,
  REDIS_URL: process.env.REDIS_URL || '',
  REDIS_USER: process.env.REDIS_USER as string,
  S3_API_IMAGE_ACCESS_KEY: process.env.S3_API_IMAGE_ACCESS_KEY as string,
  S3_API_IMAGE_SECRET_KEY: process.env.S3_API_IMAGE_SECRET_KEY as string,
  SECRET_COOKIE_PASSWORD: process.env.SECRET_COOKIE_PASSWORD as string,
  SENTRY: process.env.NEXT_PUBLIC_SENTRY_DSN as string,
  SKIP_REDIS: process.env.SKIP_REDIS !== undefined,
  TALKJS_APP_ID: (process.env.NEXT_PUBLIC_TALKJS_APP_ID || process.env.TALKJS_APP_ID) as string,
  TALKJS_SECRET_KEY: process.env.TALKJS_SECRET_KEY as string,
};
